// Offline storage using IndexedDB
const DB_NAME = 'ourcashflow_offline';
const DB_VERSION = 1;

const STORES = {
  sites: 'sites',
  entities: 'entities',
  subCategories: 'subCategories',
  entries: 'entries',
  entityBankAccounts: 'entityBankAccounts',
  syncQueue: 'syncQueue',
  meta: 'meta',
};

function openDB() {
  return new Promise((resolve, reject) => {
    const request = indexedDB.open(DB_NAME, DB_VERSION);
    request.onerror = () => reject(request.error);
    request.onsuccess = () => resolve(request.result);
    request.onupgradeneeded = (event) => {
      const db = event.target.result;
      // Data stores
      if (!db.objectStoreNames.contains(STORES.sites)) db.createObjectStore(STORES.sites, { keyPath: '_id' });
      if (!db.objectStoreNames.contains(STORES.entities)) db.createObjectStore(STORES.entities, { keyPath: '_id' });
      if (!db.objectStoreNames.contains(STORES.subCategories)) db.createObjectStore(STORES.subCategories, { keyPath: '_id' });
      if (!db.objectStoreNames.contains(STORES.entries)) db.createObjectStore(STORES.entries, { keyPath: '_id' });
      if (!db.objectStoreNames.contains(STORES.entityBankAccounts)) db.createObjectStore(STORES.entityBankAccounts, { keyPath: '_id' });
      // Queue for offline mutations
      if (!db.objectStoreNames.contains(STORES.syncQueue)) {
        const store = db.createObjectStore(STORES.syncQueue, { keyPath: 'id', autoIncrement: true });
        store.createIndex('timestamp', 'timestamp');
      }
      // Meta store for timestamps etc
      if (!db.objectStoreNames.contains(STORES.meta)) db.createObjectStore(STORES.meta, { keyPath: 'key' });
    };
  });
}

// Generic CRUD helpers
async function putAll(storeName, items) {
  const db = await openDB();
  const tx = db.transaction(storeName, 'readwrite');
  const store = tx.objectStore(storeName);
  // Clear old data and put new
  store.clear();
  for (const item of items) {
    store.put(item);
  }
  return new Promise((resolve, reject) => {
    tx.oncomplete = () => { db.close(); resolve(); };
    tx.onerror = () => { db.close(); reject(tx.error); };
  });
}

async function getAll(storeName) {
  const db = await openDB();
  const tx = db.transaction(storeName, 'readonly');
  const store = tx.objectStore(storeName);
  return new Promise((resolve, reject) => {
    const req = store.getAll();
    req.onsuccess = () => { db.close(); resolve(req.result); };
    req.onerror = () => { db.close(); reject(req.error); };
  });
}

async function setMeta(key, value) {
  const db = await openDB();
  const tx = db.transaction(STORES.meta, 'readwrite');
  tx.objectStore(STORES.meta).put({ key, value, updatedAt: Date.now() });
  return new Promise((resolve, reject) => {
    tx.oncomplete = () => { db.close(); resolve(); };
    tx.onerror = () => { db.close(); reject(tx.error); };
  });
}

async function getMeta(key) {
  const db = await openDB();
  const tx = db.transaction(STORES.meta, 'readonly');
  const req = tx.objectStore(STORES.meta).get(key);
  return new Promise((resolve, reject) => {
    req.onsuccess = () => { db.close(); resolve(req.result?.value || null); };
    req.onerror = () => { db.close(); reject(req.error); };
  });
}

// Sync queue operations
async function addToSyncQueue(action) {
  const db = await openDB();
  const tx = db.transaction(STORES.syncQueue, 'readwrite');
  tx.objectStore(STORES.syncQueue).add({
    ...action,
    timestamp: Date.now(),
    retries: 0,
  });
  return new Promise((resolve, reject) => {
    tx.oncomplete = () => { db.close(); resolve(); };
    tx.onerror = () => { db.close(); reject(tx.error); };
  });
}

async function getSyncQueue() {
  return getAll(STORES.syncQueue);
}

async function removeSyncItem(id) {
  const db = await openDB();
  const tx = db.transaction(STORES.syncQueue, 'readwrite');
  tx.objectStore(STORES.syncQueue).delete(id);
  return new Promise((resolve, reject) => {
    tx.oncomplete = () => { db.close(); resolve(); };
    tx.onerror = () => { db.close(); reject(tx.error); };
  });
}

async function clearSyncQueue() {
  const db = await openDB();
  const tx = db.transaction(STORES.syncQueue, 'readwrite');
  tx.objectStore(STORES.syncQueue).clear();
  return new Promise((resolve, reject) => {
    tx.oncomplete = () => { db.close(); resolve(); };
    tx.onerror = () => { db.close(); reject(tx.error); };
  });
}

async function getSyncQueueCount() {
  const db = await openDB();
  const tx = db.transaction(STORES.syncQueue, 'readonly');
  const req = tx.objectStore(STORES.syncQueue).count();
  return new Promise((resolve, reject) => {
    req.onsuccess = () => { db.close(); resolve(req.result); };
    req.onerror = () => { db.close(); reject(req.error); };
  });
}

export const offlineDB = {
  // Data stores
  sites: {
    save: (items) => putAll(STORES.sites, items),
    getAll: () => getAll(STORES.sites),
  },
  entities: {
    save: (items) => putAll(STORES.entities, items),
    getAll: () => getAll(STORES.entities),
  },
  subCategories: {
    save: (items) => putAll(STORES.subCategories, items),
    getAll: () => getAll(STORES.subCategories),
  },
  entries: {
    save: (items) => putAll(STORES.entries, items),
    getAll: () => getAll(STORES.entries),
  },
  entityBankAccounts: {
    save: (items) => putAll(STORES.entityBankAccounts, items),
    getAll: () => getAll(STORES.entityBankAccounts),
  },
  // Sync queue
  syncQueue: {
    add: addToSyncQueue,
    getAll: getSyncQueue,
    remove: removeSyncItem,
    clear: clearSyncQueue,
    count: getSyncQueueCount,
  },
  // Meta
  meta: {
    set: setMeta,
    get: getMeta,
  },
};
